import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  MenuItem,
  Typography,
  Box,
  Chip,
  Alert,
  ToggleButtonGroup,
  ToggleButton,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Paper,
  Stack
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import axios from 'axios';
import dayjs from 'dayjs';
import { getTimezoneLabel } from '../../../utils/timezoneUtils';
import AMPChecksManager from '../../amp/AMPChecksManager';

const mapScheduleCheckToFormCheck = (check, index) => ({
  sequenceNumber: check.sequenceNumber || index + 1,
  sequenceLabel: check.sequenceLabel || '',
  description: check.description || '',
  taskReferenceId: check.taskReferenceId || '',
  ampCheckSequenceNumber: check.sequenceNumber || index + 1,
  requiredSpares: check.requiredSpares || [],
  requiredConsumables: check.requiredConsumables || [],
  requiredToolsEquipment: check.requiredToolsEquipment || []
});

const mapParentCheckToFormCheck = (check, index) => ({
  sequenceNumber: check.sequenceNumber || index + 1,
  sequenceLabel: check.sequenceLabel || '',
  description: check.description || '',
  taskReferenceId: check.taskReferenceId || '',
  ampCheckSequenceNumber: check.ampCheckSequenceNumber || check.sequenceNumber || index + 1,
  requiredSpares: check.requiredSpares || [],
  requiredConsumables: check.requiredConsumables || [],
  requiredToolsEquipment: check.requiredToolsEquipment || [],
  sourceParentAmpCheckId: check._id || undefined
});

const checkSelectionKey = (check, index) =>
  String(check.ampCheckSequenceNumber || check.sequenceNumber || index + 1);

const parentCheckSelectionKey = (check, index) =>
  String(check._id || `seq-${check.ampCheckSequenceNumber || check.sequenceNumber || index + 1}`);

/** Combine a calendar date with hours/minutes from a time value. */
const combineDateAndTime = (dateValue, timeValue, defaultHour = 0, defaultMinute = 0) => {
  if (!dateValue) return null;
  const base = dayjs(dateValue);
  if (!base.isValid()) return null;
  const t = timeValue ? dayjs(timeValue) : null;
  return base
    .hour(t && t.isValid() ? t.hour() : defaultHour)
    .minute(t && t.isValid() ? t.minute() : defaultMinute)
    .second(0)
    .millisecond(0);
};

const isSameCalendarDay = (left, right) => {
  if (!left || !right) return false;
  return dayjs(left).format('YYYY-MM-DD') === dayjs(right).format('YYYY-MM-DD');
};

/**
 * Form dialog for creating/editing maintenance work orders
 */
const MaintenanceFormDialog = ({
  open,
  onClose,
  formData,
  setFormData,
  aircraft,
  selectedMaintenance,
  onSubmit,
  displayTimezone,
  standaloneEmergencyMode = false,
  edrRaiseMode = false,
  edrContext = null,
  lockAircraft = false,
  submitLabel,
  scheduledDateDisabled = false,
  scheduledDateHelperText
}) => {
  const [ampSchedules, setAMPSchedules] = useState([]);
  const [loadingAMPSchedules, setLoadingAMPSchedules] = useState(false);
  const [intimations, setIntimations] = useState([]);
  const [loadingIntimations, setLoadingIntimations] = useState(false);
  const [addIntimationValue, setAddIntimationValue] = useState('');

  useEffect(() => {
    if (open) {
      fetchAMPSchedules();
      if (selectedMaintenance) fetchIntimations();
    } else {
      setAMPSchedules([]);
      setIntimations([]);
    }
  }, [open, selectedMaintenance]);

  const mapScheduleCheckToFormRow = (check, index) => mapScheduleCheckToFormCheck(check, index);

  const applyMappedScheduleChecks = (schedule, selectedKeys) => {
    const scheduleChecks = Array.isArray(schedule?.ampChecks) ? schedule.ampChecks : [];
    const keySet = new Set(selectedKeys);
    return scheduleChecks
      .map((check, index) => ({ check, index, key: checkSelectionKey(check, index) }))
      .filter(({ key }) => keySet.has(key))
      .map(({ check, index }, mappedIndex) => ({
        ...mapScheduleCheckToFormCheck(check, index),
        sequenceNumber: mappedIndex + 1
      }));
  };

  const parentAmpChecks = Array.isArray(edrContext?.parentAmpChecks) ? edrContext.parentAmpChecks : [];
  const useParentFailedCheckSelection = edrRaiseMode && parentAmpChecks.length > 0;

  // EDR prefill may set ampScheduleId before schedules load — sync type/assemblies (and map schedule checks only when parent has none)
  useEffect(() => {
    if (!open || !edrRaiseMode || !formData.ampScheduleId || loadingAMPSchedules) return;
    const selected = ampSchedules.find((s) => (s._id || s).toString() === String(formData.ampScheduleId));
    if (!selected) return;
    setFormData((prev) => {
      if (String(prev.ampScheduleId || '') !== String(formData.ampScheduleId)) return prev;
      const nextAssemblies = selected.assemblies
        ? selected.assemblies.map((a) => (a._id || a).toString())
        : (prev.assemblies || []);
      const nextMaintenanceType = selected.maintenanceType || prev.maintenanceType || '';
      const assembliesUnchanged =
        JSON.stringify(nextAssemblies) === JSON.stringify(prev.assemblies || []);
      const typeUnchanged = nextMaintenanceType === (prev.maintenanceType || '');

      // When parent WO has checks, CAMO selects which failed — do not auto-map the full schedule
      if (useParentFailedCheckSelection) {
        if (typeUnchanged && assembliesUnchanged) return prev;
        return {
          ...prev,
          maintenanceType: nextMaintenanceType,
          assemblies: nextAssemblies
        };
      }
      if (Array.isArray(prev.ampChecks) && prev.ampChecks.length > 0) {
        if (typeUnchanged && assembliesUnchanged) return prev;
        return {
          ...prev,
          maintenanceType: nextMaintenanceType,
          assemblies: nextAssemblies
        };
      }
      if (!selected.ampChecks?.length) {
        if (typeUnchanged && assembliesUnchanged) return prev;
        return {
          ...prev,
          maintenanceType: nextMaintenanceType,
          assemblies: nextAssemblies
        };
      }
      const allKeys = selected.ampChecks.map((check, index) => checkSelectionKey(check, index));
      return {
        ...prev,
        maintenanceType: nextMaintenanceType,
        assemblies: nextAssemblies,
        ampChecks: applyMappedScheduleChecks(selected, allKeys)
      };
    });
  }, [open, edrRaiseMode, formData.ampScheduleId, ampSchedules, loadingAMPSchedules, useParentFailedCheckSelection]);

  const fetchIntimations = async () => {
    setLoadingIntimations(true);
    try {
      const res = await axios.get('/api/intimations');
      setIntimations(res.data || []);
    } catch (err) {
      console.error('Error fetching intimations:', err);
      setIntimations([]);
    } finally {
      setLoadingIntimations(false);
    }
  };

  const fetchAMPSchedules = async () => {
    setLoadingAMPSchedules(true);
    try {
      const response = await axios.get('/api/amp-schedules');
      setAMPSchedules(response.data || []);
    } catch (error) {
      console.error('Error fetching AMP schedules:', error);
      setAMPSchedules([]);
    } finally {
      setLoadingAMPSchedules(false);
    }
  };

  const handleAircraftChange = (e) => {
    const aircraftId = e.target.value;
    setFormData({
      ...formData,
      aircraftId,
      ampScheduleId: '',
      maintenanceType: '',
      assemblies: [],
      ampChecks: []
    });
  };

  const handleAMPScheduleChange = (e) => {
    const ampScheduleId = e.target.value || '';
    const selected = ampSchedules.find((s) => (s._id || s).toString() === ampScheduleId);
    if (edrRaiseMode) {
      // Keep already-selected failed parent checks; only remapping from schedule when parent has none
      if (useParentFailedCheckSelection) {
        setFormData({
          ...formData,
          ampScheduleId,
          maintenanceType: selected ? selected.maintenanceType : '',
          assemblies: selected && selected.assemblies ? selected.assemblies.map((a) => (a._id || a).toString()) : []
        });
        return;
      }
      const scheduleChecks = Array.isArray(selected?.ampChecks) ? selected.ampChecks : [];
      const allKeys = scheduleChecks.map((check, index) => checkSelectionKey(check, index));
      setFormData({
        ...formData,
        ampScheduleId,
        maintenanceType: selected ? selected.maintenanceType : '',
        assemblies: selected && selected.assemblies ? selected.assemblies.map((a) => (a._id || a).toString()) : [],
        // Default: map all AMP checks; CAMO can deselect
        ampChecks: ampScheduleId ? applyMappedScheduleChecks(selected, allKeys) : []
      });
      return;
    }

    const mappedChecks = selected?.ampChecks?.length
      ? selected.ampChecks.map((check, index) => mapScheduleCheckToFormCheck(check, index))
      : [];
    setFormData({
      ...formData,
      ampScheduleId,
      maintenanceType: selected ? selected.maintenanceType : '',
      assemblies: selected && selected.assemblies ? selected.assemblies.map((a) => (a._id || a).toString()) : [],
      ampChecks: ampScheduleId ? mappedChecks : (formData.ampChecks || [])
    });
  };

  const applySelectedParentFailedChecks = (selectedKeys) => {
    const keySet = new Set(selectedKeys);
    const selected = parentAmpChecks
      .map((check, index) => ({ check, index, key: parentCheckSelectionKey(check, index) }))
      .filter(({ key }) => keySet.has(key))
      .map(({ check, index }, mappedIndex) => ({
        ...mapParentCheckToFormCheck(check, index),
        sequenceNumber: mappedIndex + 1
      }));
    setFormData({
      ...formData,
      ampChecks: selected
    });
  };

  const selectedParentFailedKeys = new Set(
    (Array.isArray(formData.ampChecks) ? formData.ampChecks : [])
      .map((check) => {
        if (check.sourceParentAmpCheckId) return String(check.sourceParentAmpCheckId);
        const matchIdx = parentAmpChecks.findIndex(
          (p) =>
            String(p.ampCheckSequenceNumber || p.sequenceNumber || '') ===
              String(check.ampCheckSequenceNumber || check.sequenceNumber || '') &&
            String(p.description || '') === String(check.description || '')
        );
        return matchIdx >= 0 ? parentCheckSelectionKey(parentAmpChecks[matchIdx], matchIdx) : null;
      })
      .filter(Boolean)
  );

  const handleToggleParentFailedCheck = (check, index, checked) => {
    const key = parentCheckSelectionKey(check, index);
    const nextKeys = new Set(selectedParentFailedKeys);
    if (checked) nextKeys.add(key);
    else nextKeys.delete(key);
    applySelectedParentFailedChecks([...nextKeys]);
  };

  const handleSelectAllParentFailedChecks = (selectAll) => {
    const allKeys = selectAll
      ? parentAmpChecks.map((check, index) => parentCheckSelectionKey(check, index))
      : [];
    applySelectedParentFailedChecks(allKeys);
  };

  const selectedAmpSchedule = ampSchedules.find(
    (s) => (s._id || s).toString() === String(formData.ampScheduleId || '')
  );
  const scheduleChecksForMapping = Array.isArray(selectedAmpSchedule?.ampChecks)
    ? selectedAmpSchedule.ampChecks
    : [];
  const mappedSelectionKeys = new Set(
    (Array.isArray(formData.ampChecks) ? formData.ampChecks : [])
      .map((check) => String(check.ampCheckSequenceNumber || check.sequenceNumber || ''))
      .filter(Boolean)
  );

  const handleToggleMappedAmpCheck = (check, index, checked) => {
    const key = checkSelectionKey(check, index);
    const nextKeys = new Set(mappedSelectionKeys);
    if (checked) nextKeys.add(key);
    else nextKeys.delete(key);
    setFormData({
      ...formData,
      ampChecks: applyMappedScheduleChecks(selectedAmpSchedule, [...nextKeys])
    });
  };

  const handleSelectAllMappedAmpChecks = (selectAll) => {
    const allKeys = selectAll
      ? scheduleChecksForMapping.map((check, index) => checkSelectionKey(check, index))
      : [];
    setFormData({
      ...formData,
      ampChecks: applyMappedScheduleChecks(selectedAmpSchedule, allKeys)
    });
  };

  const showChecklistEditor = !selectedMaintenance && !standaloneEmergencyMode;

  const ampSchedulesForAircraft = formData.aircraftId
    ? ampSchedules.filter((s) => (s.aircraftId?._id || s.aircraftId)?.toString() === formData.aircraftId.toString())
    : ampSchedules;

  const linkedIds = Array.isArray(formData.linkedIntimationIds) ? formData.linkedIntimationIds : [];
  const linkedIdSet = new Set(linkedIds.map((id) => (id && (id._id || id)).toString()));
  const addableIntimations = intimations.filter((i) => !linkedIdSet.has((i._id || i).toString()));
  const linkedForDisplay = linkedIds.map((id) => {
    const obj = intimations.find((i) => (i._id || i).toString() === (id && (id._id || id)).toString());
    return { id: id && (id._id || id), title: obj?.title, status: obj?.status };
  });

  const handleAddIntimation = (intimationId) => {
    if (!intimationId) return;
    const id = intimationId.toString();
    if (linkedIdSet.has(id)) return;
    setFormData({
      ...formData,
      linkedIntimationIds: [...linkedIds.map((i) => i._id || i), id]
    });
    setAddIntimationValue('');
  };
  const handleRemoveIntimation = (idToRemove) => {
    const id = (idToRemove && (idToRemove._id || idToRemove)).toString();
    setFormData({
      ...formData,
      linkedIntimationIds: linkedIds.filter((i) => (i && (i._id || i)).toString() !== id).map((i) => i._id || i)
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth={showChecklistEditor ? 'lg' : 'md'} fullWidth>
      <DialogTitle>
        {selectedMaintenance
          ? 'Edit Maintenance'
          : edrRaiseMode
            ? 'Create work order from EDR'
            : standaloneEmergencyMode
              ? 'Stand-alone emergency work order'
              : 'Schedule New Maintenance'}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {edrRaiseMode && edrContext && !selectedMaintenance && (
            <>
              <Grid item xs={12}>
                <Alert severity="info">
                  Create a regular work order from the Engineering Defect Report on parent work order{' '}
                  <strong>{edrContext.parentWorkOrderNumber}</strong>
                  {edrContext.parentTitle ? ` — ${edrContext.parentTitle}` : ''}. The new work order will appear in
                  the main work order list and follow the standard maintenance workflow.
                </Alert>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Work order number"
                  value={edrContext.suggestedWorkOrderNumber || ''}
                  InputProps={{ readOnly: true }}
                  helperText="Assigned automatically (parent WO + suffix)"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Parent work order"
                  value={edrContext.parentWorkOrderNumber || ''}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              {(edrContext.edrSummary?.edrReferenceNumber ||
                edrContext.edrSummary?.icplNumber ||
                edrContext.edrSummary?.defectDetails ||
                edrContext.edrSummary?.proposedRectification) && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    EDR summary (from MRO)
                  </Typography>
                  <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                    {edrContext.edrSummary.edrReferenceNumber && (
                      <Typography variant="body2">
                        <strong>EDR reference:</strong> {edrContext.edrSummary.edrReferenceNumber}
                      </Typography>
                    )}
                    {edrContext.edrSummary.icplNumber && (
                      <Typography variant="body2">
                        <strong>Authorisation number:</strong> {edrContext.edrSummary.icplNumber}
                      </Typography>
                    )}
                    {edrContext.edrSummary.defectDetails && (
                      <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                        <strong>Defect:</strong> {edrContext.edrSummary.defectDetails}
                      </Typography>
                    )}
                    {edrContext.edrSummary.proposedRectification && (
                      <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                        <strong>Proposed rectification:</strong> {edrContext.edrSummary.proposedRectification}
                      </Typography>
                    )}
                  </Box>
                </Grid>
              )}
            </>
          )}
          {standaloneEmergencyMode && !selectedMaintenance && (
            <Grid item xs={12}>
              <Alert severity="warning">
                This work order is created in <strong>SCHEDULED</strong> status. CAMO QM must review and acknowledge it
                like a normal work order before Planning can act. Available to Super Admin, Accountable Manager, CA Manager,
                CA Technical Officer, and Planning Manager. Inform Sales and Operations — future charters may be cancelled.
              </Alert>
            </Grid>
          )}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              select
              label="Aircraft"
              value={formData.aircraftId}
              onChange={handleAircraftChange}
              disabled={lockAircraft}
              helperText={lockAircraft ? 'Aircraft is fixed to match the parent work order.' : undefined}
            >
              {aircraft.map((ac) => (
                <MenuItem key={ac._id} value={ac._id}>
                  {ac.registration} - {ac.manufacturer} {ac.model}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              select
              label="Type"
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
            >
              <MenuItem value="PHASE_INSPECTION">Phase Inspection</MenuItem>
              <MenuItem value="OUT_OF_PHASE_INSPECTION">Out of phase Inspection</MenuItem>
              <MenuItem value="LUBRICATION_INSPECTION">Lubrication Inspection</MenuItem>
              <MenuItem value="CONDITIONAL_INSPECTION">Conditional Inspection</MenuItem>
              <MenuItem value="ONE_TIME_INSPECTIONS">One Time Inspections</MenuItem>
              <MenuItem value="CORROSION_PREVENTION_AND_CONTROL">Corrosion Prevention and Control</MenuItem>
              <MenuItem value="INSPECTION">Inspection</MenuItem>
              <MenuItem value="MAINTENANCE">Maintenance</MenuItem>
              <MenuItem value="REPAIR">Defect</MenuItem>
              <MenuItem value="SCHEDULED">Scheduled</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Creation source / reason"
              value={formData.creationSourceRemarks || ''}
              onChange={(e) => setFormData({ ...formData, creationSourceRemarks: e.target.value })}
              placeholder={selectedMaintenance ? 'Optional: source or reason for this work order' : 'Required: e.g. Manual schedule, Request from Ops, Follow-up from defect...'}
              required={!selectedMaintenance}
              helperText={!selectedMaintenance ? 'Required when the work order is not linked to an intimation.' : 'Source or reason for creating this work order (optional when linked to intimations).'}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Maintenance CSV (optional)
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Button variant="outlined" component="label" size="small">
                Upload CSV
                <input
                  hidden
                  type="file"
                  accept=".csv,text/csv"
                  onChange={(e) => {
                    const file = e.target.files?.[0] || null;
                    setFormData({ ...formData, workOrderCsvFile: file });
                  }}
                />
              </Button>
              {formData.workOrderCsvFile?.name && (
                <Typography variant="caption" color="text.secondary">
                  Selected: {formData.workOrderCsvFile.name}
                </Typography>
              )}
              {!formData.workOrderCsvFile && selectedMaintenance?.workOrderCsvDocument?.originalName && (
                <Typography variant="caption" color="text.secondary">
                  Current: {selectedMaintenance.workOrderCsvDocument.originalName}
                </Typography>
              )}
            </Box>
          </Grid>

          {selectedMaintenance && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 0.5, mb: 0.5 }}>
                Linked intimations
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1 }}>
                {linkedForDisplay.map(({ id, title, status }) => (
                  <Chip
                    key={id}
                    label={title ? `${title} (${status || '–'})` : `Intimation ${String(id).slice(-6)}`}
                    size="small"
                    onDelete={() => handleRemoveIntimation(id)}
                    sx={{ maxWidth: 280 }}
                  />
                ))}
                <TextField
                  select
                  size="small"
                  label="Add intimation"
                  value={addIntimationValue}
                  disabled={loadingIntimations || addableIntimations.length === 0}
                  onChange={(e) => handleAddIntimation(e.target.value)}
                  sx={{ minWidth: 220 }}
                  SelectProps={{ displayEmpty: true, renderValue: (v) => v ? (addableIntimations.find((i) => (i._id || i).toString() === v)?.title || 'Intimation') : (addableIntimations.length === 0 ? (loadingIntimations ? 'Loading…' : 'No other intimations') : 'Add intimation…') }}
                >
                  <MenuItem value="">{addableIntimations.length === 0 ? (loadingIntimations ? 'Loading…' : 'None to add') : 'Select…'}</MenuItem>
                  {addableIntimations.map((i) => (
                    <MenuItem key={i._id} value={i._id}>
                      {i.title} ({i.status || '–'})
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
              <Typography variant="caption" color="text.secondary">
                Link additional intimations fulfilled by this work order, or remove links. Changes save on Update.
              </Typography>
            </Grid>
          )}

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              select
              label="Priority"
              value={formData.priority}
              onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
            >
              <MenuItem value="LOW">Low</MenuItem>
              <MenuItem value="MEDIUM">Medium</MenuItem>
              <MenuItem value="HIGH">High</MenuItem>
              <MenuItem value="CRITICAL">Critical</MenuItem>
            </TextField>
          </Grid>
          {(!standaloneEmergencyMode && !edrRaiseMode) || selectedMaintenance ? (
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                select
                label="Status"
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              >
                <MenuItem value="SCHEDULED">Scheduled</MenuItem>
                <MenuItem value="HOLD">Hold</MenuItem>
                <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
                <MenuItem value="PARTS_PROCURED">Parts Procured</MenuItem>
                <MenuItem value="COMPLETED">Completed</MenuItem>
                <MenuItem value="CANCELLED">Cancelled</MenuItem>
              </TextField>
            </Grid>
          ) : null}
          <Grid
            item
            xs={12}
          >
            <Typography variant="body2" gutterBottom>
              Schedule type
            </Typography>
            <ToggleButtonGroup
              value={formData.scheduleMode === 'HOURLY' ? 'HOURLY' : 'FULL_DAY'}
              exclusive
              disabled={Boolean(scheduledDateDisabled)}
              onChange={(e, v) => {
                if (!v) return;
                if (v === 'FULL_DAY') {
                  setFormData({
                    ...formData,
                    scheduleMode: v,
                    scheduledEndDate: null
                  });
                  return;
                }
                const start = formData.scheduledDate
                  ? (combineDateAndTime(formData.scheduledDate, formData.scheduledDate, 6, 0) || dayjs(formData.scheduledDate).hour(6).minute(0))
                  : null;
                setFormData({
                  ...formData,
                  scheduleMode: v,
                  scheduledDate: start,
                  scheduledEndDate: formData.scheduledEndDate
                    || (start ? start.add(2, 'hour') : null)
                });
              }}
              fullWidth
              size="small"
            >
              <ToggleButton value="FULL_DAY">Full day</ToggleButton>
              <ToggleButton value="HOURLY">Time window</ToggleButton>
            </ToggleButtonGroup>
            {formData.scheduleMode === 'HOURLY' ? (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                Same start/end date → times on that day. Different dates → blocks from start date/time through end date/time.
              </Typography>
            ) : null}
          </Grid>
          {formData.scheduleMode === 'HOURLY' ? (
            <>
              <Grid item xs={12} md={6}>
                <DatePicker
                  label={displayTimezone ? `Start date (${getTimezoneLabel(displayTimezone)})` : 'Start date'}
                  value={formData.scheduledDate}
                  onChange={(date) => {
                    if (!date) {
                      setFormData({ ...formData, scheduledDate: null });
                      return;
                    }
                    const nextStart = combineDateAndTime(date, formData.scheduledDate, 6, 0);
                    const endWasSameDay = !formData.scheduledEndDate || isSameCalendarDay(formData.scheduledDate, formData.scheduledEndDate);
                    let nextEnd;
                    if (endWasSameDay) {
                      nextEnd = combineDateAndTime(
                        date,
                        formData.scheduledEndDate || (nextStart ? nextStart.add(2, 'hour') : null),
                        8,
                        0
                      );
                      if (nextStart && nextEnd && !nextEnd.isAfter(nextStart)) {
                        nextEnd = nextStart.add(2, 'hour');
                      }
                    } else {
                      nextEnd = combineDateAndTime(formData.scheduledEndDate, formData.scheduledEndDate, 8, 0);
                      if (nextStart && nextEnd && dayjs(nextEnd).startOf('day').isBefore(dayjs(nextStart).startOf('day'))) {
                        nextEnd = combineDateAndTime(date, formData.scheduledEndDate, 8, 0);
                        if (nextEnd && !nextEnd.isAfter(nextStart)) nextEnd = nextStart.add(2, 'hour');
                      }
                    }
                    setFormData({
                      ...formData,
                      scheduledDate: nextStart,
                      scheduledEndDate: nextEnd
                    });
                  }}
                  disabled={Boolean(scheduledDateDisabled)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      helperText:
                        scheduledDateHelperText ||
                        (scheduledDateDisabled
                          ? 'Only Accountable Manager, Planning Manager (until MRO handover), or CAMO team (while the work order is with CAMO) can change this date.'
                          : undefined)
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DatePicker
                  label={displayTimezone ? `End date (${getTimezoneLabel(displayTimezone)})` : 'End date'}
                  value={formData.scheduledEndDate || formData.scheduledDate}
                  minDate={formData.scheduledDate || undefined}
                  onChange={(date) => {
                    if (!date) return;
                    if (formData.scheduledDate && dayjs(date).startOf('day').isBefore(dayjs(formData.scheduledDate).startOf('day'))) {
                      return;
                    }
                    const nextEnd = combineDateAndTime(date, formData.scheduledEndDate || formData.scheduledDate, 8, 0);
                    if (
                      formData.scheduledDate &&
                      nextEnd &&
                      isSameCalendarDay(formData.scheduledDate, date) &&
                      !nextEnd.isAfter(dayjs(formData.scheduledDate))
                    ) {
                      setFormData({ ...formData, scheduledEndDate: dayjs(formData.scheduledDate).add(2, 'hour') });
                      return;
                    }
                    setFormData({ ...formData, scheduledEndDate: nextEnd });
                  }}
                  disabled={Boolean(scheduledDateDisabled) || !formData.scheduledDate}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      helperText: !formData.scheduledDate ? 'Select start date first' : undefined
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TimePicker
                  label={isSameCalendarDay(formData.scheduledDate, formData.scheduledEndDate || formData.scheduledDate) ? 'Start time (same day)' : 'Start time'}
                  value={formData.scheduledDate}
                  onChange={(time) => {
                    const nextStart = combineDateAndTime(formData.scheduledDate || dayjs(), time, 6, 0);
                    setFormData({
                      ...formData,
                      scheduledDate: nextStart,
                      scheduledEndDate:
                        nextStart &&
                        formData.scheduledEndDate &&
                        isSameCalendarDay(nextStart, formData.scheduledEndDate) &&
                        !dayjs(formData.scheduledEndDate).isAfter(nextStart)
                          ? nextStart.add(2, 'hour')
                          : formData.scheduledEndDate
                    });
                  }}
                  ampm={false}
                  disabled={Boolean(scheduledDateDisabled) || !formData.scheduledDate}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      helperText: !formData.scheduledDate ? 'Select start date first' : undefined
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TimePicker
                  label={isSameCalendarDay(formData.scheduledDate, formData.scheduledEndDate || formData.scheduledDate) ? 'End time (same day)' : 'End time'}
                  value={formData.scheduledEndDate}
                  onChange={(time) => {
                    setFormData({
                      ...formData,
                      scheduledEndDate: combineDateAndTime(
                        formData.scheduledEndDate || formData.scheduledDate || dayjs(),
                        time,
                        8,
                        0
                      )
                    });
                  }}
                  ampm={false}
                  disabled={Boolean(scheduledDateDisabled) || !formData.scheduledDate}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      helperText: formData.scheduledDate && formData.scheduledEndDate && isSameCalendarDay(formData.scheduledDate, formData.scheduledEndDate)
                        ? 'Times apply on this day only'
                        : formData.scheduledDate && formData.scheduledEndDate
                          ? `Window runs until ${dayjs(formData.scheduledEndDate).format('YYYY-MM-DD')}`
                          : (!formData.scheduledDate ? 'Select start date first' : undefined)
                    }
                  }}
                />
              </Grid>
              {formData.scheduledDate && (formData.scheduledEndDate || formData.scheduledDate) ? (
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ py: 0.5 }}>
                    {isSameCalendarDay(formData.scheduledDate, formData.scheduledEndDate || formData.scheduledDate)
                      ? `Same-day window: ${dayjs(formData.scheduledDate).format('YYYY-MM-DD')} ${dayjs(formData.scheduledDate).format('HH:mm')}–${dayjs(formData.scheduledEndDate || formData.scheduledDate).format('HH:mm')}`
                      : `Multi-day window: ${dayjs(formData.scheduledDate).format('YYYY-MM-DD HH:mm')} → ${dayjs(formData.scheduledEndDate).format('YYYY-MM-DD HH:mm')}`}
                  </Alert>
                </Grid>
              ) : null}
            </>
          ) : (
            <Grid
              item
              xs={12}
              md={(standaloneEmergencyMode || edrRaiseMode) && !selectedMaintenance ? 6 : 4}
            >
              <DatePicker
                label={displayTimezone ? `Scheduled Date (${getTimezoneLabel(displayTimezone)})` : 'Scheduled Date'}
                value={formData.scheduledDate}
                onChange={(date) => setFormData({ ...formData, scheduledDate: date })}
                disabled={Boolean(scheduledDateDisabled)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    helperText:
                      scheduledDateHelperText ||
                      (scheduledDateDisabled
                        ? 'Only Accountable Manager, Planning Manager (until MRO handover), or CAMO team (while the work order is with CAMO) can change this date.'
                        : undefined)
                  }
                }}
              />
            </Grid>
          )}
          {formData.type === 'SCHEDULED' && (
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                select
                label="Inspection Type"
                value={formData.inspectionType || ''}
                onChange={(e) => setFormData({ ...formData, inspectionType: e.target.value })}
              >
                <MenuItem value="">None</MenuItem>
                <MenuItem value="A_CHECK">A-Check</MenuItem>
                <MenuItem value="B_CHECK">B-Check</MenuItem>
                <MenuItem value="C_CHECK">C-Check</MenuItem>
                <MenuItem value="D_CHECK">D-Check</MenuItem>
                <MenuItem value="OTHER">Other</MenuItem>
              </TextField>
            </Grid>
          )}

          {showChecklistEditor && edrRaiseMode && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2, mb: 0.5 }}>
                {useParentFailedCheckSelection
                  ? 'Select the check that failed (required)'
                  : 'Map AMP checks to this EDR work order (required)'}
              </Typography>
              <Alert severity="info" sx={{ mb: 1.5 }}>
                {useParentFailedCheckSelection
                  ? 'Choose the checklist item(s) on the parent work order that failed / caused this EDR. Selected checks are copied onto the new EDR work order for the MRO Engineer to complete.'
                  : 'Select an AMP schedule for this aircraft, then map the AMP checks that apply to this EDR work order. Mapped checks are copied onto the new work order for the MRO Engineer to complete.'}
              </Alert>
              <TextField
                fullWidth
                select
                required
                label="AMP Schedule *"
                value={formData.ampScheduleId || ''}
                onChange={handleAMPScheduleChange}
                disabled={loadingAMPSchedules}
                helperText={
                  loadingAMPSchedules
                    ? 'Loading AMP schedules…'
                    : ampSchedulesForAircraft.length === 0
                      ? 'No AMP schedules found for this aircraft. Create one under AMP Schedules first.'
                      : useParentFailedCheckSelection
                        ? 'Required. Links the EDR work order to an AMP schedule (defaults to the parent work order schedule).'
                        : 'Required. Choose the AMP schedule whose checks you will map to this EDR work order.'
                }
                sx={{ mb: 1.5 }}
              >
                <MenuItem value="">Select AMP schedule</MenuItem>
                {ampSchedulesForAircraft.map((schedule) => (
                  <MenuItem key={schedule._id} value={schedule._id}>
                    {schedule.title}
                    {schedule.aircraftId?.registration && ` — ${schedule.aircraftId.registration}`}
                    {` (${schedule.ampChecks?.length || 0} checks)`}
                    {schedule.maintenanceType === 'LINE_INSPECTION' && ' (Line)'}
                    {schedule.maintenanceType === 'BASE_INSPECTION' && ' (Base)'}
                  </MenuItem>
                ))}
              </TextField>

              {useParentFailedCheckSelection && (
                <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
                    <Typography variant="subtitle2">
                      Parent work order checks ({selectedParentFailedKeys.size}/{parentAmpChecks.length} selected)
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <Button size="small" onClick={() => handleSelectAllParentFailedChecks(true)}>Select all</Button>
                      <Button size="small" onClick={() => handleSelectAllParentFailedChecks(false)}>Clear</Button>
                    </Stack>
                  </Stack>
                  <FormGroup>
                    {parentAmpChecks.map((check, index) => {
                      const key = parentCheckSelectionKey(check, index);
                      const label = (check.sequenceLabel && String(check.sequenceLabel).trim())
                        || check.sequenceNumber
                        || index + 1;
                      const statusLabel = check.status ? String(check.status).replace(/_/g, ' ') : '';
                      return (
                        <FormControlLabel
                          key={`${key}-${index}`}
                          control={(
                            <Checkbox
                              checked={selectedParentFailedKeys.has(key)}
                              onChange={(e) => handleToggleParentFailedCheck(check, index, e.target.checked)}
                            />
                          )}
                          label={(
                            <Box>
                              <Typography variant="body2">
                                <strong>S/N {label}</strong>
                                {check.taskReferenceId ? ` · ${check.taskReferenceId}` : ''}
                                {statusLabel ? ` · ${statusLabel}` : ''}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {check.description || '(No description)'}
                              </Typography>
                            </Box>
                          )}
                          sx={{ alignItems: 'flex-start', mb: 0.5 }}
                        />
                      );
                    })}
                  </FormGroup>
                </Paper>
              )}

              {!useParentFailedCheckSelection && formData.ampScheduleId && scheduleChecksForMapping.length === 0 && (
                <Alert severity="warning" sx={{ mb: 1.5 }}>
                  This AMP schedule has no checks to map. Choose another schedule or add checks on the AMP schedule first.
                </Alert>
              )}

              {!useParentFailedCheckSelection && formData.ampScheduleId && scheduleChecksForMapping.length > 0 && (
                <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
                    <Typography variant="subtitle2">
                      Select AMP checks to map ({mappedSelectionKeys.size}/{scheduleChecksForMapping.length})
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <Button size="small" onClick={() => handleSelectAllMappedAmpChecks(true)}>Select all</Button>
                      <Button size="small" onClick={() => handleSelectAllMappedAmpChecks(false)}>Clear</Button>
                    </Stack>
                  </Stack>
                  <FormGroup>
                    {scheduleChecksForMapping.map((check, index) => {
                      const key = checkSelectionKey(check, index);
                      const label = (check.sequenceLabel && String(check.sequenceLabel).trim())
                        || check.sequenceNumber
                        || index + 1;
                      return (
                        <FormControlLabel
                          key={`${key}-${index}`}
                          control={(
                            <Checkbox
                              checked={mappedSelectionKeys.has(key)}
                              onChange={(e) => handleToggleMappedAmpCheck(check, index, e.target.checked)}
                            />
                          )}
                          label={(
                            <Box>
                              <Typography variant="body2">
                                <strong>S/N {label}</strong>
                                {check.taskReferenceId ? ` · ${check.taskReferenceId}` : ''}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {check.description || '(No description)'}
                              </Typography>
                            </Box>
                          )}
                          sx={{ alignItems: 'flex-start', mb: 0.5 }}
                        />
                      );
                    })}
                  </FormGroup>
                </Paper>
              )}
            </Grid>
          )}

          {showChecklistEditor && !edrRaiseMode && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2, mb: 0.5 }}>
                Checklist (required) — enter checks manually
              </Typography>
              <Alert severity="info" sx={{ mb: 1.5 }}>
                CAMO must add at least one checklist item when raising this work order.
                Use <strong>Add checklist item</strong> to enter each check (description required).
                Optionally link an AMP schedule below to pre-fill items, then edit them.
                The MRO Engineer completes these checks later with Tech + AME signatures.
              </Alert>
              <AMPChecksManager
                title="Manual checklist"
                addLabel="Add checklist item"
                emptyMessage='No checklist items yet. Click "Add checklist item" to enter one manually.'
                checks={Array.isArray(formData.ampChecks) ? formData.ampChecks : []}
                onChange={(ampChecks) => setFormData({ ...formData, ampChecks })}
              />
            </Grid>
          )}

          {/* Link to existing AMP Schedule (optional) – pre-fills checklist for non-EDR create */}
          {showChecklistEditor && !edrRaiseMode && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2, mb: 0.5 }}>
                Link to AMP Schedule (Optional)
              </Typography>
              <TextField
                fullWidth
                select
                label="AMP Schedule"
                value={formData.ampScheduleId || ''}
                onChange={handleAMPScheduleChange}
                disabled={loadingAMPSchedules}
                helperText="Optional. Selecting a schedule pre-fills the checklist above so you can edit it. To create a new AMP schedule, use Aircraft Maintenance Program (AMP) Schedules."
              >
                <MenuItem value="">None</MenuItem>
                {ampSchedulesForAircraft.map((schedule) => (
                  <MenuItem key={schedule._id} value={schedule._id}>
                    {schedule.title}
                    {schedule.aircraftId?.registration && ` — ${schedule.aircraftId.registration}`}
                    {schedule.maintenanceType === 'LINE_INSPECTION' && ' (Line Inspection)'}
                    {schedule.maintenanceType === 'BASE_INSPECTION' && ' (Base Inspection)'}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={onSubmit} variant="contained">
          {submitLabel ||
            (selectedMaintenance ? 'Update' : standaloneEmergencyMode ? 'Create stand-alone WO' : edrRaiseMode ? 'Create work order' : 'Schedule')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MaintenanceFormDialog;
